/**
 * Alert Preferences Component
 *
 * Per Plan: docs/USER_DEFINED_ALERTS_PLAN.md
 *
 * Main UI for configuring user alert preferences:
 * - Global settings (enable/disable, severity threshold)
 * - Topic preferences (performance, anomalies, opportunities, risks)
 * - Timing preferences (realtime, digest, quiet hours)
 * - Delivery channels (in-app, Slack, email)
 * - Brand scope (include/exclude specific brands)
 * - Rate limiting settings
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Snackbar,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
  Slider,
  Autocomplete
} from '@mui/material';
import {
  Notifications,
  Schedule,
  FilterList,
  Speed,
  Send,
  Save,
  Refresh,
  Info,
  NotificationsActive,
  NotificationsOff,
  Email,
  Chat,
  CalendarMonth
} from '@mui/icons-material';
import alertPreferencesService from '../../services/alertPreferencesService';
import { brandService } from '../../services/brandService';

// Severity options ordered by priority
const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical Only', description: 'Only the most urgent alerts' },
  { value: 'high', label: 'High & Above', description: 'High priority and critical alerts' },
  { value: 'medium', label: 'Medium & Above', description: 'Most important alerts (recommended)' },
  { value: 'low', label: 'All Alerts', description: 'Receive all alert types' }
];

// Topic options
const TOPIC_OPTIONS = [
  { value: 'performance', label: 'Performance', description: 'KPI changes, metrics updates' },
  { value: 'anomalies', label: 'Anomalies', description: 'Unusual patterns detected' },
  { value: 'opportunities', label: 'Opportunities', description: 'Growth and optimization suggestions' },
  { value: 'risks', label: 'Risks', description: 'Potential issues and warnings' }
];

// Delivery mode options
const DELIVERY_MODE_OPTIONS = [
  { value: 'realtime', label: 'Realtime', description: 'Get alerts immediately as they happen' },
  { value: 'digest', label: 'Digest', description: 'Receive batched summaries at scheduled times' }
];

// Digest frequency options
const DIGEST_FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' }
];

// Common timezone options
const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney'
];

const AlertPreferences = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [brands, setBrands] = useState([]);

  // Schedule state
  const [schedules, setSchedules] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(null);

  // Preferences state
  const [preferences, setPreferences] = useState({
    alerts_enabled: true,
    min_severity: 'medium',
    enabled_topics: ['performance', 'anomalies', 'opportunities', 'risks'],
    delivery_mode: 'realtime',
    digest_frequency: 'daily',
    digest_time: '08:00',
    digest_timezone: 'UTC',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
    included_brand_ids: [],
    excluded_brand_ids: [],
    delivery_channels: { in_app: true, slack: false, email: false },
    email_address: '',
    max_alerts_per_day: 50,
    max_alerts_per_hour: 10
  });

  // Load preferences and brands on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prefsResult, brandsResult, schedulesResult] = await Promise.all([
        alertPreferencesService.getPreferences(),
        brandService.getBrands(),
        alertPreferencesService.getSchedules().catch(() => ({ schedules: [] }))
      ]);

      if (prefsResult.preferences) {
        // Parse delivery_channels if it's a string
        const prefs = prefsResult.preferences;
        if (typeof prefs.delivery_channels === 'string') {
          try {
            prefs.delivery_channels = JSON.parse(prefs.delivery_channels);
          } catch (e) {
            prefs.delivery_channels = { in_app: true, slack: false, email: false };
          }
        }
        setPreferences(prev => ({ ...prev, ...prefs }));
      }

      if (brandsResult.brands) {
        setBrands(brandsResult.brands);
      }

      if (schedulesResult.schedules) {
        setSchedules(schedulesResult.schedules);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load preferences. Using defaults.',
        severity: 'warning'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle preference changes
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  // Handle topic toggle
  const handleTopicToggle = (topic) => {
    setPreferences(prev => {
      const topics = prev.enabled_topics || [];
      if (topics.includes(topic)) {
        return { ...prev, enabled_topics: topics.filter(t => t !== topic) };
      } else {
        return { ...prev, enabled_topics: [...topics, topic] };
      }
    });
  };

  // Handle delivery channel toggle
  const handleChannelToggle = (channel) => {
    setPreferences(prev => ({
      ...prev,
      delivery_channels: {
        ...prev.delivery_channels,
        [channel]: !prev.delivery_channels[channel]
      }
    }));
  };

  // Save preferences
  const handleSave = async () => {
    setSaving(true);
    try {
      await alertPreferencesService.updatePreferences(preferences);
      setSnackbar({
        open: true,
        message: 'Preferences saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save preferences. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Schedule management helpers
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const parseCronDays = (cron) => {
    const parts = (cron || '').trim().split(/\s+/);
    if (parts.length !== 5) return [];
    const dow = parts[4];
    if (dow === '*') return [0, 1, 2, 3, 4, 5, 6];
    return dow.split(',').map(d => parseInt(d)).filter(d => !isNaN(d));
  };

  const parseCronHour = (cron) => {
    const parts = (cron || '').trim().split(/\s+/);
    if (parts.length !== 5) return 6;
    return parseInt(parts[1]) || 0;
  };

  const buildCron = (days, hour) => {
    if (days.length === 0) return `0 ${hour} * * *`;
    if (days.length === 7) return `0 ${hour} * * *`;
    return `0 ${hour} * * ${days.sort((a, b) => a - b).join(',')}`;
  };

  const handleScheduleDayToggle = (scheduleId, day) => {
    setSchedules(prev => prev.map(s => {
      if (s.id !== scheduleId) return s;
      const days = parseCronDays(s.cron_expression);
      const newDays = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
      return { ...s, cron_expression: buildCron(newDays, parseCronHour(s.cron_expression)) };
    }));
  };

  const handleScheduleHourChange = (scheduleId, hour) => {
    setSchedules(prev => prev.map(s => {
      if (s.id !== scheduleId) return s;
      const days = parseCronDays(s.cron_expression);
      return { ...s, cron_expression: buildCron(days, hour) };
    }));
  };

  const handleScheduleToggle = (scheduleId) => {
    setSchedules(prev => prev.map(s => {
      if (s.id !== scheduleId) return s;
      return { ...s, is_enabled: s.is_enabled ? 0 : 1 };
    }));
  };

  const handleSaveSchedule = async (schedule) => {
    setSavingSchedule(schedule.id);
    try {
      await alertPreferencesService.updateSchedule(schedule.id, {
        cron_expression: schedule.cron_expression,
        is_enabled: schedule.is_enabled
      });
      setSnackbar({ open: true, message: `${schedule.label} schedule updated!`, severity: 'success' });
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSnackbar({ open: true, message: 'Failed to save schedule.', severity: 'error' });
    } finally {
      setSavingSchedule(null);
    }
  };

  // Send test alert
  const handleSendTestAlert = async () => {
    setSendingTest(true);
    try {
      await alertPreferencesService.sendTestAlert();
      setSnackbar({
        open: true,
        message: 'Test alert sent! Check your enabled delivery channels.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending test alert:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send test alert.',
        severity: 'error'
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" color="primary">
          Alert Preferences
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Global Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {preferences.alerts_enabled ? (
                  <NotificationsActive sx={{ mr: 1, color: 'success.main' }} />
                ) : (
                  <NotificationsOff sx={{ mr: 1, color: 'text.disabled' }} />
                )}
                <Typography variant="h6">Global Settings</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.alerts_enabled}
                    onChange={handleChange('alerts_enabled')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Enable Alerts</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Master switch for all alert notifications
                    </Typography>
                  </Box>
                }
                sx={{ mb: 3, alignItems: 'flex-start' }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Minimum Severity</InputLabel>
                <Select
                  value={preferences.min_severity}
                  label="Minimum Severity"
                  onChange={handleChange('min_severity')}
                  disabled={!preferences.alerts_enabled}
                >
                  {SEVERITY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box>
                        <Typography variant="body1">{opt.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                Currently set to receive <strong>{SEVERITY_OPTIONS.find(s => s.value === preferences.min_severity)?.label || 'Medium & Above'}</strong> alerts
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Topic Preferences */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FilterList sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Topics</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which types of alerts you want to receive
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TOPIC_OPTIONS.map(topic => (
                  <Tooltip key={topic.value} title={topic.description} arrow>
                    <Chip
                      label={topic.label}
                      color={preferences.enabled_topics?.includes(topic.value) ? 'primary' : 'default'}
                      onClick={() => handleTopicToggle(topic.value)}
                      disabled={!preferences.alerts_enabled}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Box>

              {preferences.enabled_topics?.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No topics selected. You won't receive any alerts.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Timing */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Delivery Timing</Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Delivery Mode</InputLabel>
                <Select
                  value={preferences.delivery_mode}
                  label="Delivery Mode"
                  onChange={handleChange('delivery_mode')}
                  disabled={!preferences.alerts_enabled}
                >
                  {DELIVERY_MODE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box>
                        <Typography variant="body1">{opt.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {preferences.delivery_mode === 'digest' && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={preferences.digest_frequency}
                          label="Frequency"
                          onChange={handleChange('digest_frequency')}
                        >
                          {DIGEST_FREQUENCY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Delivery Time"
                        type="time"
                        value={preferences.digest_time}
                        onChange={handleChange('digest_time')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>

                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={preferences.digest_timezone}
                      label="Timezone"
                      onChange={handleChange('digest_timezone')}
                    >
                      {TIMEZONE_OPTIONS.map(tz => (
                        <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.quiet_hours_enabled}
                    onChange={handleChange('quiet_hours_enabled')}
                  />
                }
                label="Enable Quiet Hours"
                sx={{ mb: 2 }}
              />

              {preferences.quiet_hours_enabled && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={preferences.quiet_hours_start}
                      onChange={handleChange('quiet_hours_start')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={preferences.quiet_hours_end}
                      onChange={handleChange('quiet_hours_end')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Channels */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Notifications sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Delivery Channels</Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.delivery_channels?.in_app}
                      onChange={() => handleChannelToggle('in_app')}
                      disabled={!preferences.alerts_enabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <NotificationsActive sx={{ mr: 1, fontSize: 20 }} />
                      <Typography>In-App Notifications</Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.delivery_channels?.slack}
                      onChange={() => handleChannelToggle('slack')}
                      disabled={!preferences.alerts_enabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chat sx={{ mr: 1, fontSize: 20 }} />
                      <Typography>Slack Direct Messages</Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.delivery_channels?.email}
                      onChange={() => handleChannelToggle('email')}
                      disabled={!preferences.alerts_enabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Email sx={{ mr: 1, fontSize: 20 }} />
                      <Typography>Email Alerts</Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', mb: 2 }}
                />

                {preferences.delivery_channels?.email && (
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={preferences.email_address || ''}
                    onChange={handleChange('email_address')}
                    placeholder="Optional: Use different email for alerts"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Button
                variant="outlined"
                startIcon={sendingTest ? <CircularProgress size={20} /> : <Send />}
                onClick={handleSendTestAlert}
                disabled={sendingTest || !preferences.alerts_enabled}
                fullWidth
              >
                Send Test Alert
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Alert Schedules */}
        {schedules.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CalendarMonth sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Alert Schedules</Typography>
                  <Tooltip title="Configure when each alert type runs. Select days and time for each schedule.">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {schedules.map(schedule => {
                  const days = parseCronDays(schedule.cron_expression);
                  const hour = parseCronHour(schedule.cron_expression);

                  return (
                    <Box key={schedule.id} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!schedule.is_enabled}
                                onChange={() => handleScheduleToggle(schedule.id)}
                              />
                            }
                            label={<Typography variant="subtitle1" fontWeight="bold">{schedule.label}</Typography>}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={savingSchedule === schedule.id ? <CircularProgress size={16} /> : <Save />}
                          onClick={() => handleSaveSchedule(schedule)}
                          disabled={savingSchedule === schedule.id}
                        >
                          Save
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Days:</Typography>
                        {DAY_NAMES.map((name, idx) => (
                          <Chip
                            key={idx}
                            label={name}
                            size="small"
                            color={days.includes(idx) ? 'primary' : 'default'}
                            variant={days.includes(idx) ? 'filled' : 'outlined'}
                            onClick={() => handleScheduleDayToggle(schedule.id, idx)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}

                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Time:</Typography>
                          <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                              value={hour}
                              onChange={(e) => handleScheduleHourChange(schedule.id, e.target.value)}
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      {schedule.next_run && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Next run: {new Date(schedule.next_run).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Brand Scope */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FilterList sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Brand Scope</Typography>
                <Tooltip title="Filter alerts by specific brands. Leave empty to receive alerts for all brands.">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Autocomplete
                multiple
                options={brands}
                getOptionLabel={(option) => option.name || ''}
                value={brands.filter(b => preferences.included_brand_ids?.includes(b.id))}
                onChange={(e, newValue) => {
                  setPreferences(prev => ({
                    ...prev,
                    included_brand_ids: newValue.map(b => b.id)
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Include Only These Brands"
                    placeholder="All brands if empty"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                      color="primary"
                      size="small"
                    />
                  ))
                }
                disabled={!preferences.alerts_enabled}
                sx={{ mb: 3 }}
              />

              <Autocomplete
                multiple
                options={brands}
                getOptionLabel={(option) => option.name || ''}
                value={brands.filter(b => preferences.excluded_brand_ids?.includes(b.id))}
                onChange={(e, newValue) => {
                  setPreferences(prev => ({
                    ...prev,
                    excluded_brand_ids: newValue.map(b => b.id)
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Exclude These Brands"
                    placeholder="None excluded if empty"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                      color="error"
                      size="small"
                    />
                  ))
                }
                disabled={!preferences.alerts_enabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Rate Limiting */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Speed sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Rate Limiting</Typography>
                <Tooltip title="Prevent alert fatigue by limiting the number of alerts you receive">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" gutterBottom>
                  Max alerts per hour: <strong>{preferences.max_alerts_per_hour}</strong>
                </Typography>
                <Slider
                  value={preferences.max_alerts_per_hour}
                  onChange={(e, value) => setPreferences(prev => ({ ...prev, max_alerts_per_hour: value }))}
                  min={1}
                  max={50}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' }
                  ]}
                  disabled={!preferences.alerts_enabled}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Max alerts per day: <strong>{preferences.max_alerts_per_day}</strong>
                </Typography>
                <Slider
                  value={preferences.max_alerts_per_day}
                  onChange={(e, value) => setPreferences(prev => ({ ...prev, max_alerts_per_day: value }))}
                  min={5}
                  max={200}
                  marks={[
                    { value: 5, label: '5' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' }
                  ]}
                  disabled={!preferences.alerts_enabled}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AlertPreferences;
